/**
 * Navigate to an EXTERNAL url after confirming it is a well-formed https:// URL.
 *
 * Assigning a javascript:/data: scheme value to window.location.href executes script (XSS).
 * Our checkout/portal URLs come from the backend (Stripe); we validate the scheme as defense
 * in depth. Returns false and does NOT navigate on a malformed or non-https URL.
 *
 * NOTE: this is for absolute external URLs only (https). Do NOT use it for same-origin route
 * paths like '/login' — those are not absolute https URLs and will be (correctly) rejected.
 *
 * `targetWindow` lets callers redirect a DIFFERENT window (e.g. a checkout tab opened via
 * window.open) instead of the current one. Defaults to the current window. Returns false if
 * the target window is null/closed (popup blocked) so the caller can fall back.
 */
export function safeExternalRedirect(
  url: string | null | undefined,
  targetWindow: Window | null = typeof window !== 'undefined' ? window : null,
): boolean {
  if (!url) {return false;}
  if (!targetWindow || targetWindow.closed) {return false;}
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    console.error('Blocked redirect: malformed URL');
    return false;
  }
  if (parsed.protocol !== 'https:') {
    console.error(`Blocked redirect: non-https scheme "${parsed.protocol}"`);
    return false;
  }
  targetWindow.location.href = parsed.href;
  return true;
}
